import { Box, Typography, Menu, MenuItem, Button, Snackbar, Alert, Checkbox, FormControlLabel } from '@mui/material';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { AiOutlineClose } from "react-icons/ai";
import { useSelector } from 'react-redux';
import CustomButton from './CustomButton';
import Draggable from 'react-draggable';
import tableIcon from '../assets/img/table.png';
import QRCode from "react-qr-code";
import Select from 'react-select';
import { toPng } from 'html-to-image';
import { HiOutlineDotsVertical } from "react-icons/hi";
import { generateAllQRCodes, generateRoomQRCodes } from './qrGeneratorApi';

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

const ModalQRGeneratorTable = ({ showModal, locationId, closeModal = () => { } }) => {
  const [configTabValue, setConfigTabValue] = useState(0);
  const [selectedTableName, setSelectedTableName] = useState('');
  const { loading, configurationData } = useSelector((state) => state.Location);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [tableList, setTableList] = useState([]);
  const [selectedTableId, setSelectedTableId] = useState('');
  const [QRValue, setQRValue] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [isGeneratingAll, setIsGeneratingAll] = useState(false);
  const [isGeneratingRoom, setIsGeneratingRoom] = useState(false);
  const [locationLogo, setLocationLogo] = useState('');
  const [includeLogo, setIncludeLogo] = useState(true);
  const [includeTableName, setIncludeTableName] = useState(true); // NEW

  // Get location logo from localStorage
  useEffect(() => {
    let locInfo = localStorage.getItem('formData');
    if (locInfo) {
      locInfo = JSON.parse(locInfo);
      setLocationLogo(locInfo.logo_image || '');
    }
  }, []);

  const handleChangeConfigTab = (newValue) => {
    setConfigTabValue(newValue);
  };

  const handleSelectTable = (e) => {
    setSelectedTableName(e.label);
    setSelectedTableId(e.value);
    const isStageUrl = window.location.href.includes('stage');
    const urlToMap = isStageUrl
      ? `https://stage.adhocorder.ro/product/${locationId}?${e.value}`
      : `https://adhocorder.ro/product/${locationId}?${e.value}`;
    setQRValue(urlToMap);
  };

  const handleDownloadJPG = async () => {
    const qrElement = document.getElementById('QRcodeRoomTable');
    if (qrElement) {
      try {
        await new Promise(resolve => setTimeout(resolve, 100));
        const dataUrl = await toPng(qrElement, {
          quality: 0.95,
          cacheBust: true,
          pixelRatio: 2,
          backgroundColor: '#FFFFFF'
        });
        const a = document.createElement('a');
        a.href = dataUrl;
        let displayName = selectedTableName || selectedRoom?.name || 'pickup';
        a.download = `${displayName}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        showSnackbar('QR code downloaded successfully!', 'success');
      } catch (error) {
        console.error('Conversion to PNG failed:', error);
        showSnackbar('Failed to download QR code', 'error');
      }
    } else {
      showSnackbar('QR code not found', 'error');
    }
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const generateQRUrl = (tableId = null) => {
    const isStageUrl = window.location.href.includes('stage');
    const baseUrl = isStageUrl
      ? `https://stage.adhocorder.ro/product/${locationId}`
      : `https://adhocorder.ro/product/${locationId}`;
    return tableId ? `${baseUrl}?${tableId}` : baseUrl;
  };

  const handleDownloadAllQR = async () => {
    setIsGeneratingAll(true);
    handleMenuClose();
    try {
      const rooms = configurationData[0]?.rooms || [];
      const environment = window.location.href.includes('stage') ? 'stage' : 'production';
      showSnackbar('Generating QR codes, please wait...', 'info');
      await generateAllQRCodes(locationId, rooms, environment, includeLogo, includeTableName);
      showSnackbar('All QR codes downloaded successfully!', 'success');
    } catch (error) {
      console.error('Error generating all QR codes:', error);
      showSnackbar('Failed to generate QR codes. Please try again.', 'error');
    } finally {
      setIsGeneratingAll(false);
    }
  };

  const handleDownloadRoomQR = async () => {
    if (!selectedRoom) {
      showSnackbar('Please select a room first', 'warning');
      return;
    }
    setIsGeneratingRoom(true);
    handleMenuClose();
    try {
      const environment = window.location.href.includes('stage') ? 'stage' : 'production';
      showSnackbar(`Generating QR codes for ${selectedRoom.name}...`, 'info');
      await generateRoomQRCodes(locationId, selectedRoom, environment, includeLogo, includeTableName);
      showSnackbar(`${selectedRoom.name} QR codes downloaded successfully!`, 'success');
    } catch (error) {
      console.error('Error generating room QR codes:', error);
      showSnackbar('Failed to generate room QR codes. Please try again.', 'error');
    } finally {
      setIsGeneratingRoom(false);
    }
  };

  useEffect(() => {
    if (configurationData.length > 0) {
      const { rooms } = configurationData[0];
      setSelectedRoom(rooms[0]);
    }
  }, [configurationData]);

  useEffect(() => {
    if (showModal) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }
  }, [showModal]);

  useEffect(() => {
    setQRValue('');
    if (selectedRoom) {
      let mappedDt;
      if (selectedRoom.tables) {
        mappedDt = selectedRoom.tables.map(val => ({ value: val.id, label: val.name }));
      }
      setTableList(mappedDt);
      setSelectedTableName('');
      setSelectedTableId('');
      if (selectedRoom.id === 'pick_del_id') {
        setQRValue(generateQRUrl());
        setSelectedTableName('Pickup / Delivery');
      }
    }
  }, [selectedRoom]);

  const getQRDisplayName = () => {
    if (selectedTableName) return selectedTableName;
    if (selectedRoom?.id === 'pick_del_id') return 'Pickup / Delivery';
    if (selectedRoom?.name) return selectedRoom.name;
    return 'QR Code';
  };

  return (
    <>
      <div className={`${!showModal && 'hidden'} left-0 top-0 h-full w-full bg-opacity-60 bg-black fixed z-30 flex justify-center items-center`}>
        <div className='bg-white overflow-y-scroll rounded-lg p-6 w-5/6 h-5/6'>
          <div className='lg:grid lg:grid-cols-4 h-full lg:gap-5'>
            <div className='col-span-3'>
              <div className='text-xl lg:text-3xl font-semibold flex justify-between'>
                QR Code Generator for {`${selectedRoom?.name ? selectedRoom.name + ' /' : ''} ${selectedTableName ?? ''}`}
                <span className='lg:hidden'><AiOutlineClose onClick={closeModal} size={20} /></span>
              </div>
              <Box className='room-tab-section mt-5'>
                <div className='room-tab flex space-x-7'>
                  {configurationData[0]?.rooms?.map((room, index) => (
                    <div
                      className={`flex items-center cursor-pointer py-5 ${configTabValue === index && 'text-primary700 border-b-2 border-primary700'}`}
                      key={index}
                      onClick={() => {
                        setSelectedRoom(room);
                        handleChangeConfigTab(index);
                      }}
                    >
                      <div>{room?.name}</div>
                    </div>
                  ))}
                  <div
                    className={`flex items-center cursor-pointer py-5 ${configTabValue === (configurationData[0]?.rooms?.length || 0) && 'text-primary700 border-b-2 border-primary700'}`}
                    key={configurationData[0]?.rooms?.length || 0}
                    onClick={() => {
                      const selRoomDel = {
                        id: 'pick_del_id',
                        name: "Pickup/Delivery",
                        tables: []
                      };
                      setSelectedRoom(selRoomDel);
                      handleChangeConfigTab(configurationData[0]?.rooms?.length || 0);
                    }}
                  >
                    <div>Pickup/Delivery</div>
                  </div>
                </div>
                <Box className='room-tab-detail'>
                  {configurationData[0]?.rooms?.map((room, roomIndex) => (
                    <CustomTabPanel value={configTabValue} index={roomIndex} key={roomIndex}>
                      {room?.tables?.map((table, tableIndex) => (
                        <Box className='table-box' key={tableIndex}>
                          <Draggable position={{ x: table.x, y: table.y }}>
                            <Box
                              className={`${selectedTableId === table?.id && '!bg-primaryColor'} first-room`}
                              style={{
                                backgroundImage: `url(${tableIcon})`,
                                backgroundSize: '43px',
                                backgroundPosition: 'center',
                                backgroundRepeat: 'no-repeat',
                                backgroundPosition: '90%',
                                width: `${100 + (table.seats > 2 ? (table.seats - 2) * 5.5 : 0)}px`,
                                height: '65px',
                                minWidth: '100px',
                                maxWidth: '200px'
                              }}
                            >
                              <Typography>{table?.name}</Typography>
                            </Box>
                          </Draggable>
                        </Box>
                      ))}
                    </CustomTabPanel>
                  ))}
                </Box>
              </Box>
            </div>
            <div className='w-full'>
              <div className='hidden cursor-pointer lg:flex justify-end'>
                <AiOutlineClose onClick={closeModal} size={30} />
              </div>
              <div className='border mb-3 mt-5 border-[#D9D9D9] rounded-xl p-3 w-full lg:h-[90%] flex flex-col justify-between'>
                <div>
                  {QRValue && (
                    <div
                      id="QRcodeRoomTable"
                      style={{
                        backgroundColor: '#ffffff',
                        borderRadius: '16px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        display: 'inline-block',
                        width: '100%'
                      }}
                    >
                      <div style={{ position: 'relative', display: 'flex', justifyContent: 'center' }}>
                        <QRCode
                          value={QRValue}
                          size={256}
                          level="H"
                          bgColor="#ffffff"
                          fgColor="#000000"
                          style={{ maxWidth: '100%', height: 'auto' }}
                        />
                        {includeLogo && locationLogo && (
                          <div
                            style={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              transform: 'translate(-50%, -50%)',
                              width: '35%',
                              maxWidth: '90px',
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              zIndex: 2,
                              pointerEvents: 'none'
                            }}
                          >
                            <img
                              src={locationLogo}
                              alt="Location Logo"
                              style={{
                                width: '100%',
                                height: 'auto',
                                objectFit: 'contain'
                              }}
                            />
                          </div>
                        )}
                      </div>
                      {/* Table name block - conditional */}
                      {includeTableName && (
                        <div style={{
                          marginTop: '16px',
                          textAlign: 'center',
                          borderTop: '1px solid #e0e0e0',
                          paddingTop: '12px'
                        }}>
                          <Typography variant="subtitle1" style={{ fontWeight: 600, color: '#333' }}>
                            {getQRDisplayName()}
                          </Typography>
                        </div>
                      )}
                    </div>
                  )}
                  {selectedRoom?.id !== 'pick_del_id' && (
                    <Select
                      className='mt-4'
                      name="tableList"
                      options={tableList}
                      placeholder="Select Table name"
                      value={selectedTableId && tableList && tableList.find((ele) => ele.value === selectedTableId)}
                      onChange={handleSelectTable}
                    />
                  )}
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={includeLogo}
                        onChange={(e) => setIncludeLogo(e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Include Logo in QR"
                    style={{ marginTop: '12px', display: 'block' }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={includeTableName}
                        onChange={(e) => setIncludeTableName(e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Include Table Name in QR"
                    style={{ marginTop: '8px', display: 'block' }}
                  />
                </div>
                <div className='lg:hidden h-20'></div>

                <div className='space-y-3'>
                  <CustomButton
                    onClickButton={handleDownloadJPG}
                    className={'!w-full justify-center'}
                    label={'Download Current PNG'}
                    customBg={'bg-[#FFB800]'}
                  />
                  <div className='relative'>
                    <Button
                      onClick={handleMenuClick}
                      variant="contained"
                      className='!w-full justify-center !bg-[#4CAF50] hover:!bg-[#45a049]'
                      startIcon={<HiOutlineDotsVertical />}
                    >
                      Batch Download Options
                    </Button>
                    <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                      <MenuItem onClick={handleDownloadRoomQR} disabled={isGeneratingRoom || isGeneratingAll}>
                        {isGeneratingRoom ? 'Generating...' : `Download ${selectedRoom?.name || 'Room'} QR Codes`}
                      </MenuItem>
                      <MenuItem onClick={handleDownloadAllQR} disabled={isGeneratingAll || isGeneratingRoom}>
                        {isGeneratingAll ? 'Generating All...' : 'Download All QR Codes (All Rooms)'}
                      </MenuItem>
                    </Menu>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

ModalQRGeneratorTable.propTypes = {
  showModal: PropTypes.bool.isRequired,
  locationId: PropTypes.string.isRequired,
  closeModal: PropTypes.func
};

export default ModalQRGeneratorTable;